import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Edit2, XCircle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { meetingsAPI } from '../../services/apiService';
import { useMsal } from "@azure/msal-react";
import { protectedResources } from '../../utils/msalConfig';
import { cancelMeeting, updateMeetingDetails  } from '../../services/teamsService';


const AttendeesModal = ({ attendees,meeting, onClose }) => {
    if (!attendees) return null;

    const handleExport = () => {
        const csvContent = "Name,Email\n" + 
            attendees.map(attendee => `${attendee.name},${attendee.email}`).join("\n");
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        const formattedDate = new Date(meeting.date).toISOString().split('T')[0];
        const filename = `${meeting.title}_${formattedDate}_${meeting.start_time}.csv`;
        
        link.href = url;
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Attendees List</h2>
                    <button onClick={onClose} className="text-xl font-bold text-gray-500 hover:text-gray-700">×</button>
                </div>
                <div>
                    {attendees.length === 0 ? (
                        <p className="text-gray-600">No attendees registered for this meeting.</p>
                    ) : (
                        <>
                        <table className="w-full table-auto border-collapse text-gray-700">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 border-b">Name</th>
                                    <th className="px-4 py-2 border-b">Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attendees.map((attendee, index) => (
                                    <tr key={index}>
                                        <td className="px-4 py-2 border-b">{attendee.name}</td>
                                        <td className="px-4 py-2 border-b">{attendee.email}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="mt-4 flex justify-end">
                            <button onClick={handleExport}
                                className="bg-vibrantRed hover:bg-red-600 text-white px-4 py-2 rounded-md transition font-medium"
                            > 
                                Export Attendees
                            </button>
                        </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const EditMeetingModal = ({ meeting, onClose, onSave }) => {
    const [editedMeeting, setEditedMeeting] = useState({
        id: meeting.id,
        title: meeting.title,
        description: meeting.description,
        date: meeting.date,
        start_time: meeting.start_time,
        end_time: meeting.end_time,
        teams_link: meeting.teams_link,
        id_meeting: meeting.id_meeting,
        max_participants: meeting.max_participants,
        rsvp_list: meeting.rsvp_list,
        organizer: meeting.organizer,
        accessToken:meeting.accessToken,
        refreshToken:meeting.refreshToken,
        tokenExpiry:meeting.tokenExpiry
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditedMeeting(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(meeting.id, editedMeeting);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Edit Meeting</h2>
                    <button onClick={onClose} className="text-xl font-bold text-gray-500 hover:text-gray-700">×</button>
                </div>
                <form onSubmit={handleSubmit} className="w-full px-4 py-2 bg-red-900 space-y-4">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-white">Title</label>
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={editedMeeting.title}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border rounded-md text-gray-700 focus:ring-2 focus:ring-red-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-white">Description</label>
                        <textarea
                            id="description"
                            name="description"
                            value={editedMeeting.description}
                            onChange={handleChange}
                            required
                            rows="4"
                            className="w-full px-3 py-2 border rounded-md text-gray-700 focus:ring-2 focus:ring-red-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="date" className="block text-sm font-medium text-white">Date</label>
                        <input
                            type="date"
                            id="date"
                            name="date"
                            value={editedMeeting.date}
                            onChange={handleChange}
                            required
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border rounded-md text-gray-900 focus:ring-2 focus:ring-red-500"
                        />
                    </div>
                    <div className="flex space-x-4">
                        <div>
                            <label htmlFor="start_time" className="block text-sm font-medium text-white">Start Time</label>
                            <input
                                type="time"
                                id="start_time"
                                name="start_time"
                                value={editedMeeting.start_time}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border rounded-md text-gray-700 focus:ring-2 focus:ring-red-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="end_time" className="block text-sm font-medium text-white">End Time</label>
                            <input
                                type="time"
                                id="end_time"
                                name="end_time"
                                value={editedMeeting.end_time}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border rounded-md text-gray-700 focus:ring-2 focus:ring-red-500"
                            />
                        </div>
                    </div>
                    <div className="flex space-x-4 mt-4">
                        <button type="submit" className="w-full px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-700">Save Changes</button>
                        <button type="button" onClick={onClose} className="w-full px-4 py-2 bg-red-700 text-white rounded-md hover:bg-gray-700">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const MeetingsList = () => {
    const [meetings, setMeetings] = useState([]);
    const { instance } = useMsal();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMeetingAttendees, setSelectedMeetingAttendees] = useState(null);
    const [editingMeeting, setEditingMeeting] = useState(null);
    const [expandedSeries, setExpandedSeries] = useState({});
    const [meetingToDelete, setMeetingToDelete] = useState(null);

    useEffect(() => {
        const fetchMeetings = async () => {
            try {
                const response = await meetingsAPI.getMeetings();
                

                const groupedMeetings = response.reduce((acc, meeting) => {
                    const baseTitle = meeting.title.replace(/\s+\d+$/, '');
                    if (!acc[baseTitle]) {
                        acc[baseTitle] = {
                            baseTitle,
                            description: meeting.description,
                            instances: []
                        };
                    }
                    acc[baseTitle].instances.push(meeting);
                    return acc;
                }, {});

                setMeetings(Object.values(groupedMeetings));
            } catch (err) {
                setError('Failed to load meetings.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMeetings();
    }, []);

    const handleCancelMeeting = async (meeting) => {
        try {
            setLoading(true);
            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) {
                throw new Error('No accounts found. Please sign in.');
            }

            const tokenResponse = await instance.acquireTokenSilent({
                scopes: protectedResources.meetings.scopes,
                account: accounts[0]
            });

            try {
                await cancelMeeting(meeting.id_meeting, tokenResponse.accessToken);
            } catch (teamsError) {
                console.warn('Teams cancellation failed:', teamsError);
            }

            await meetingsAPI.deleteMeeting(meeting.id);

            setMeetings(prevMeetings => 
                prevMeetings.map(series => ({
                    ...series,
                    instances: series.instances.filter(instance => instance.id !== meeting.id)
                })).filter(series => series.instances.length > 0)
            );
            
        } catch (error) {
            console.error('Cancel meeting error:', error);
            setError(error.message || 'Failed to cancel meeting. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSoftDelete = async (meeting) => {
        try {
            setLoading(true);
            
            await meetingsAPI.updateMeeting(meeting.id, { ...meeting, status: 'deleted' });

            setMeetings(prevMeetings => 
                prevMeetings.map(series => ({
                    ...series,
                    instances: series.instances.filter(instance => instance.id !== meeting.id)
                })).filter(series => series.instances.length > 0)
            );
            
            setMeetingToDelete(null);
            
        } catch (error) {
            console.error('Soft delete error:', error);
            setError(error.message || 'Failed to delete meeting. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditMeeting = (meeting) => {
        setEditingMeeting(meeting);
    };

    const handleUpdateMeeting = async (meetingId, updatedData) => {
        try {
            setLoading(true);

            await updateMeetingDetails({
                ...updatedData
            }, editingMeeting.id_meeting);
            await meetingsAPI.updateMeeting(meetingId, {...updatedData, status: "active"});
    
            setMeetings(prevMeetings =>
                prevMeetings.map(series => ({
                    ...series,
                    instances: series.instances.map(instance =>
                        instance.id === meetingId
                            ? { ...instance, ...updatedData }
                            : instance
                    )
                }))
            );
            setEditingMeeting(null);
        } catch (error) {
            console.error('Update meeting error:', error);
            setError(error.message || 'Failed to update meeting. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    const handleViewAttendees = (meeting) => {
        setSelectedMeetingAttendees(meeting.rsvp_list || []);
    };

    const closeAttendeesModal = () => {
        setSelectedMeetingAttendees(null);
    };
    const handleToggleSeries  = (baseTitle) => {
        setExpandedSeries(prev => ({
          ...prev,
          [baseTitle]: !prev[baseTitle]
        }));
      };
      if (loading) {
        return <div className="text-center text-xl text-gray-600">Loading...</div>;
    }

    if (error) {
        return <div className="text-center text-xl text-red-600">{error}</div>;
    }

    return (
        <div className="p-8">
            <h1 className="text-4xl font-bold text-center font-arial text-white mb-8">Upcoming Events</h1>
            {meetings.map((meetingSeries) => (
                <div key={meetingSeries.baseTitle} className="mb-8">
                    {/* Meeting Series Header */}
                    <div 
                        className="flex justify-between items-center mb-4 cursor-pointer bg-vibrantRed text-white px-6 py-3 rounded-lg shadow-md hover:bg-red-800 transition"
                        onClick={() => handleToggleSeries(meetingSeries.baseTitle)}
                    >
                        <h2 className="text-2xl font-semibold">{meetingSeries.baseTitle}</h2>
                        <button className="text-white">
                            {expandedSeries[meetingSeries.baseTitle] ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                        </button>
                    </div>
    

                    {expandedSeries[meetingSeries.baseTitle] && (
                        <div className="space-y-4">
                            {meetingSeries.instances.map((meeting) => (
                                <div 
                                    key={meeting.id} 
                                    className="bg-gray-700 text-white rounded-lg shadow-lg p-6 border border-gray-700 hover:border-gray-500 transition"
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-2xl font-bold">{meeting.title}</h3>
                                            <p className="text-sm text-gray-300">{meeting.description}</p>
                                            <p className="text-gray-400 text-sm flex items-center mt-2">
                                                <Calendar className="inline mr-2" />
                                                {new Date(meeting.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}  
                                                <span className="px-4">|</span>  
                                                <Clock className="inline mr-2" />
                                                {meeting.start_time} - {meeting.end_time}
                                            </p>
                                            <p className='text-gray-400 text-sm flex items-center mt-2'>
                                                <MapPin className='inline mr-2'/>
                                                {meeting.rsvp_list[0].name}</p>
                                        </div>
    

                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => setSelectedMeetingAttendees(meeting.rsvp_list)}
                                                className="bg-vibrantRed hover:bg-red-600 text-white px-4 py-2 rounded-md transition font-medium"
                                            >
                                                View Attendees
                                            </button>
                                            <button
                                                onClick={() => setEditingMeeting(meeting)}
                                                className="bg-vibrantRed hover:bg-red-600 text-white px-4 py-2 rounded-md transition font-medium flex items-center"
                                            >
                                                <Edit2 className="inline mr-2" />
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleCancelMeeting(meeting)}
                                                className="bg-vibrantRed hover:bg-red-600 text-white px-4 py-2 rounded-md transition font-medium flex items-center"
                                            >
                                                <XCircle className="inline mr-2" />
                                                Cancel Meeting
                                            </button>
                                            <button
                                                onClick={() => setMeetingToDelete(meeting)}
                                                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition font-medium flex items-center"
                                            >
                                                <Trash2 className="inline mr-2" />
                                                Archive Meeting
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
    
            {selectedMeetingAttendees && (
                <AttendeesModal attendees={selectedMeetingAttendees} meeting={meetings.flatMap(series => series.instances).find(m => m.rsvp_list === selectedMeetingAttendees)} onClose={() => setSelectedMeetingAttendees(null)} />
            )}
    
            {editingMeeting && (
                <EditMeetingModal meeting={editingMeeting} onClose={() => setEditingMeeting(null)} onSave={handleUpdateMeeting} />
            )}


            {meetingToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 text-white p-6 rounded-lg max-w-md w-full mx-4">
                        <h3 className="text-xl font-bold mb-4">Confirm Archive</h3>
                        <p className="mb-6">
                            Are you sure you want to archive "{meetingToDelete.title}"? 
                            This will hide it from the list but preserve the data.
                        </p>
                        <div className="flex space-x-4">
                            <button
                                onClick={() => handleSoftDelete(meetingToDelete)}
                                disabled={loading}
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md transition font-medium disabled:opacity-50"
                            >
                                {loading ? 'Archiving...' : 'Archive'}
                            </button>
                            <button
                                onClick={() => setMeetingToDelete(null)}
                                disabled={loading}
                                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition font-medium disabled:opacity-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
    
export default MeetingsList;